import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Timer, Play, Loader2, Mail, UserCheck, Gift } from "lucide-react";

export const AutomationsCard = () => {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);
  const [testing, setTesting] = useState(false);
  const recapYear = new Date().getFullYear();
  const sendTestRecap = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/recap/test", { year: recapYear });
      toast.success(`Test recap sent to ${data.sent_to} (sample client: ${data.sample_client})`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send test recap"); } finally { setTesting(false); }
  };

  const load = () => api.get("/admin/jobs/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/jobs/run");
      toast.success(`Ran automations: ${data.activation_reminders_sent} activation reminder(s), ${data.vault_nudges_sent} vault nudge(s), ${data.quarterly_reminders_sent || 0} quarterly reminder(s)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not run automations"); } finally { setRunning(false); }
  };

  const last = status?.recent_runs?.[0];
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="automations-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[#114A31]"><Timer className="h-5 w-5" /><span className="font-heading font-semibold">Daily automations</span></div>
          <p className="mt-1 text-sm text-[#595D5A]">Runs every day at 9:00 AM ET — activation reminders (day 3 & 6), vault nudges (30/60/90 days idle), quarterly estimated-tax reminders (7 days before due dates) and the year-end recap every Jan 5.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={sendTestRecap} disabled={testing} size="sm" variant="outline" data-testid="automations-recap-test-btn" className="rounded-full border-[#E5E5E5] hover:border-[#114A31]">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Gift className="h-4 w-4 mr-1.5" /> Email me a test recap</>}
          </Button>
          <Button onClick={runNow} disabled={running} size="sm" variant="outline" data-testid="automations-run-btn" className="rounded-full border-[#E5E5E5] hover:border-[#114A31]">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Play className="h-4 w-4 mr-1.5" /> Run now</>}
          </Button>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <div className="bg-[#F9F9F8] rounded-xl p-3"><p className="text-xs text-[#595D5A] flex items-center gap-1"><UserCheck className="h-3 w-3" /> Awaiting activation</p><p className="font-heading text-xl font-bold text-[#1C1C1C]" data-testid="automations-pending">{status?.pending_activation ?? "—"}</p></div>
        <div className="bg-[#F9F9F8] rounded-xl p-3"><p className="text-xs text-[#595D5A] flex items-center gap-1"><Mail className="h-3 w-3" /> Last run reminders</p><p className="font-heading text-xl font-bold text-[#1C1C1C]">{last?.activation_reminders_sent ?? "—"}</p></div>
        <div className="bg-[#F9F9F8] rounded-xl p-3"><p className="text-xs text-[#595D5A] flex items-center gap-1"><Mail className="h-3 w-3" /> Last run nudges</p><p className="font-heading text-xl font-bold text-[#1C1C1C]">{last?.vault_nudges_sent ?? "—"}</p></div>
        <div className="bg-[#F9F9F8] rounded-xl p-3"><p className="text-xs text-[#595D5A] flex items-center gap-1"><Mail className="h-3 w-3" /> Quarterly reminders</p><p className="font-heading text-xl font-bold text-[#1C1C1C]">{last?.quarterly_reminders_sent ?? "—"}</p></div>
      </div>
      <p className="mt-2 text-xs text-[#595D5A]" data-testid="automations-last-run">{last ? `Last run ${new Date(last.ran_at).toLocaleString()}${last.forced ? " (manual)" : ""}` : "No runs yet"}</p>
    </div>
  );
};

export default AutomationsCard;
